import { todayInZone } from "../lib/format"

/**
 * Время журнала в минутах от начала выбранного дня.
 *
 * Сетка живёт в минутах, а сервер отдаёт абсолютные моменты. Между ними нужен ровно
 * один переход, и он здесь. Считается он ПО ЗОНЕ ФИЛИАЛА (ADR-007): владелец из Москвы,
 * открывший филиал во Владивостоке, видит день, который там уже наполовину прошёл.
 *
 * ПОЧЕМУ НЕ АРИФМЕТИКА СО СМЕЩЕНИЕМ. Смещение зоны не постоянно: в странах с переводом
 * часов день бывает 23 и 25 часов, и вычитание фиксированного часа даёт ошибку дважды
 * в год — ровно в тот день, когда расписание и так перепутано. Intl.DateTimeFormat
 * знает базу зон и отвечает на единственный вопрос, который здесь задаётся: сколько
 * на часах в этом городе в этот момент.
 */

const CLOCK = new Map<string, Intl.DateTimeFormat>()

function clockFor(timeZone: string): Intl.DateTimeFormat {
  const existing = CLOCK.get(timeZone)
  if (existing !== undefined) return existing
  const created = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  })
  CLOCK.set(timeZone, created)
  return created
}

/** Минуты от полуночи в зоне филиала: 13:47 → 827. */
export function minutesOfDay(instant: Date, timeZone: string): number {
  const parts = clockFor(timeZone).formatToParts(instant)
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "0")
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? "0")
  // В en-GB полночь приходит как «24», а не «00»: это разрешено стандартом.
  return (hour % 24) * 60 + minute
}

/** Разница календарных дат в днях. Обе строки — YYYY-MM-DD. */
export function dateDiffInDays(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00Z`)
  const b = Date.parse(`${to}T12:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  // Полдень взят нарочно: он переживает и перевод часов, и високосную секунду.
  return Math.round((b - a) / 86_400_000)
}

/**
 * Минуты от начала ВЫБРАННОГО дня. Может выйти за сутки в обе стороны, и это не ошибка:
 * ночная смена заканчивается в 02:00 следующего дня и обязана остаться видимой на своём
 * дне, а не исчезнуть или переехать в начало сетки.
 */
export function minutesFromDayStart(instant: string, timeZone: string, date: string): number {
  const parsed = new Date(instant)
  if (Number.isNaN(parsed.getTime())) return 0
  const localDate = todayInZone(timeZone, parsed)
  return dateDiffInDays(date, localDate) * 1440 + minutesOfDay(parsed, timeZone)
}

/** «09:00» из ScheduleDayView.opensAt → 540. Испорченное значение даёт null, а не ноль. */
export function localTimeToMinutes(value: string | null): number | null {
  if (value === null) return null
  const match = /^(\d{2}):(\d{2})/.exec(value)
  if (match === null) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  return hours * 60 + minutes
}

/** «10:00–10:45» — подпись времени на карточке. */
export function formatRange(startMinute: number, endMinute: number): string {
  return `${formatMinute(startMinute)}–${formatMinute(endMinute)}`
}

export function formatMinute(minute: number): string {
  const wrapped = ((Math.round(minute) % 1440) + 1440) % 1440
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`
}

/** «2:14» — обратный отсчёт удержания. Секунды считает интерфейс, сервер даёт момент. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds))
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`
}

/** «2 ч 30 мин» — свободное время смены в сводке дня. */
export function formatDuration(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes))
  const hours = Math.floor(safe / 60)
  const rest = safe % 60
  if (hours === 0) return `${rest} мин`
  if (rest === 0) return `${hours} ч`
  return `${hours} ч ${rest} мин`
}

/**
 * Обратный переход: календарная дата филиала плюс минуты → абсолютный момент.
 *
 * ЗАЧЕМ ОН НУЖЕН. Сетка живёт в минутах, а сервер принимает Instant: «записать на 14:30
 * шестнадцатого» надо превратить в момент времени. Без этого ни создать запись,
 * ни перенести её, ни поставить удержание нельзя.
 *
 * ПОЧЕМУ ДВА ПРОХОДА, А НЕ ПРОСТО ВЫЧЕСТЬ СМЕЩЕНИЕ. Смещение зоны зависит от МОМЕНТА,
 * а момент — это то, что мы и ищем. Поэтому смещение берётся сначала по грубой догадке
 * (местное время, прочитанное как UTC), а потом уточняется по полученному моменту.
 * Второй проход не формальность: он и ловит переход на летнее время — в зоне, где часы
 * переводят, первая догадка попадает не на ту сторону перевода примерно два дня в году.
 *
 * Неоднозначный час (тот, который в зоне с переводом назад случается дважды) разрешается
 * в первый из двух — то же правило, что и у PostgreSQL. Для России это неважно: перевода
 * часов нет с 2014 года, но кабинет работает и в зонах, где он есть.
 */
export function instantFromLocal(date: string, minute: number, timeZone: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (match === null) return new Date().toISOString()
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  // Минуты могут выйти за сутки (ночная смена до 02:00) — Date.UTC это переносит сам.
  const guess = Date.UTC(year, month - 1, day, 0, 0, 0) + minute * 60_000
  const firstOffset = zoneOffsetMinutes(timeZone, new Date(guess))
  const corrected = guess - firstOffset * 60_000
  const secondOffset = zoneOffsetMinutes(timeZone, new Date(corrected))
  return new Date(guess - secondOffset * 60_000).toISOString()
}

const ZONE_PARTS = new Map<string, Intl.DateTimeFormat>()

/** На сколько минут зона впереди UTC в этот момент. Москва → 180. */
export function zoneOffsetMinutes(timeZone: string, at: Date): number {
  let format = ZONE_PARTS.get(timeZone)
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone,
    })
    ZONE_PARTS.set(timeZone, format)
  }
  const parts = format.formatToParts(at)
  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? "0")
  const asUtc = Date.UTC(
    value("year"),
    value("month") - 1,
    value("day"),
    value("hour") % 24,
    value("minute"),
    value("second"),
  )
  return Math.round((asUtc - at.getTime()) / 60_000)
}
